import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'hooks/redux'
import { useMakeDispatchCallback } from 'hooks'
import {
  COMPLEX,
  SUBTYPES_WITH_HYSTERESIS,
  CARRY_TIMER,
  TIMER,
  CARRY,
} from 'utils/constants'
import { Tooltip } from 'components/patterns'
import { getIsEditing, getIsSimulating } from 'selectors/navigator'
import { setSelectedIdWithDrawer } from 'actions/navigator'
import { OPERATORS } from 'components/utils'
import { classNames } from 'utils/utils'
import { isNumber } from 'utils/types'
import conditionLabelText from 'components/feature/utils/conditionLabelText'
import { EvaluationIconStyled } from 'components/patterns/Icon/IconsStyled'
import TimerSimulatedValueDisplay from 'components/feature/Simulation/TimerSimulatedValueDisplay'
import { getHasNativeOutput } from 'selectors/feature/outputs'
import { getSignal } from 'selectors/feature/inputs'
import withStyle, {
  StyledHysteresisEdge,
  StyledEdgeImg,
  ContentWrapper,
  RungContent,
  ConditionContextText,
  RungLabel,
} from './ConditionLabel.style'

const { LESS_THAN, GREATER_THAN_OR_EQUAL } = OPERATORS

const hasHysteresis = (subType, carryType, parentType, operator) => {
  if (parentType === TIMER) return false

  if (!SUBTYPES_WITH_HYSTERESIS.has(subType)) return false

  if (carryType !== CARRY_TIMER && subType === CARRY) return false

  const numOperator = parseInt(operator, 10)

  return (
    parentType === COMPLEX &&
    (numOperator === LESS_THAN || numOperator === GREATER_THAN_OR_EQUAL)
  )
}

// TODO: move string configuration to i18n folder
const hystererisLabel = (hysteresis) => `H: ${hysteresis || 0}`
const ConditionLabel = (props) => {
  const {
    label,
    vertical,
    className,
    isLink,
    uri,
    hysteresis,
    operator,
    rungType,
    hasErrors,
    subType,
    carryType,
    parentType,
    evaluationResult,
    edgeEvaluation,
    timerCarrySimulationValue,
    fnId,
    signal: signalUri,
    'data-testid': dataTestId,
    ...rest
  } = props

  const isSimulating = useSelector(getIsSimulating)
  const renderLabel = (condition) =>
    label && condition && <RungLabel>{label}</RungLabel>

  const setSelectedIdCallback = useMakeDispatchCallback(setSelectedIdWithDrawer)

  const isEditing = useSelector(getIsEditing)
  const hasNativeOutput = useSelector(getHasNativeOutput, { id: fnId })
  const signal = useSelector(getSignal, { signal: signalUri }) || {}

  const hasDescription = Boolean(props.selectedInputDescription)
  const showEdgeImg = parentType === COMPLEX && hasDescription
  const showHysteresis = hasHysteresis(subType, carryType, parentType, operator)
  const isSelectable = hasDescription || isEditing
  const showText = hasDescription || isLink || hasErrors

  const handleClick = () => {
    if (isSelectable) {
      setSelectedIdCallback(uri)
    }
  }

  const conditionLabel = conditionLabelText({
    operator,
    rungType,
    subType,
    hasNativeOutput,
    signal,
    ...rest,
  })

  return (
    <ContentWrapper className={classNames(className)}>
      {renderLabel(vertical)}
      <RungContent onClick={handleClick} data-testid={dataTestId}>
        {renderLabel(!vertical)}
        {showText && (
          <>
            <ConditionContextText
              className={classNames({
                link: isLink && isSelectable,
              })}
            >
              <Tooltip title={conditionLabel}>{conditionLabel}</Tooltip>
            </ConditionContextText>
            {!isLink && (
              <StyledHysteresisEdge isSimulating={isSimulating}>
                {
                  <>
                    {isNumber(timerCarrySimulationValue) && (
                      <TimerSimulatedValueDisplay
                        value={timerCarrySimulationValue}
                      />
                    )}
                    {showHysteresis && (
                      <span>{hystererisLabel(hysteresis)}</span>
                    )}
                    {showEdgeImg && <StyledEdgeImg edge={props.edge} />}
                    {isSimulating && (
                      <EvaluationIconStyled
                        evaluation={
                          edgeEvaluation !== null &&
                          edgeEvaluation !== undefined
                            ? edgeEvaluation
                            : evaluationResult
                        }
                      />
                    )}
                  </>
                }
              </StyledHysteresisEdge>
            )}
          </>
        )}
      </RungContent>
    </ContentWrapper>
  )
}

ConditionLabel.defaultProps = {
  label: null,
  vertical: false,
  isLink: false,
  isSixteenBitCanOutput: false,
}

ConditionLabel.propTypes = {
  className: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  vertical: PropTypes.bool,
  label: PropTypes.node,
  rungType: PropTypes.string,
  hysteresis: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedInputDescription: PropTypes.string,
  operator: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  edge: PropTypes.string,
  inputConfig: PropTypes.string,
  parentType: PropTypes.string,
  ignoreCarry: PropTypes.bool,
  isSixteenBitCanOutput: PropTypes.bool,
  isLink: PropTypes.bool,
  uri: PropTypes.string,
  rungName: PropTypes.string,
  translationType: PropTypes.string,
  hasErrors: PropTypes.bool,
  subType: PropTypes.string,
  carryType: PropTypes.string,
  isSimulating: PropTypes.bool,
  evaluationResult: PropTypes.bool,
  edgeEvaluation: PropTypes.bool,
  timerCarrySimulationValue: PropTypes.any,
  fnId: PropTypes.string,
  signal: PropTypes.string,
  customDescription: PropTypes.string,
  'data-testid': PropTypes.string,
}

export default withStyle(ConditionLabel)
